import React, { useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  makeStyles,
} from "@material-ui/core";
import Close from "@material-ui/icons/Close";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import ReportProblemOutlined from "@material-ui/icons/ReportProblemOutlined";
import { CONTEXT_MAX, CONTEXT_MIN, ERROR_TINT, HARM_OPTIONS, LEGIT_OPTIONS, HelpTip, cap, humanizeLabel, pal } from "./reviewUi";

// Spam Review popups (AGN-206): fixed header (title, subtitle, close) and footer
// (hint, Cancel, confirm), a scrolling body of labelled sections.

const SOFT_TEAL = "rgba(10,71,76,.05)";
const LABEL_GREY = "#8A8A8A";

const useStyles = makeStyles((theme) => {
  const c = pal(theme);
  return {
    paper: { borderRadius: 12 },
    title: {
      position: "relative",
      padding: "20px 56px 16px 24px",
      borderBottom: `1px solid ${c.line2}`,
    },
    titleText: { fontSize: 18, fontWeight: 600, color: c.primary, lineHeight: 1.35, letterSpacing: "-.01em", wordBreak: "break-word" },
    subtitle: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 8,
      marginTop: 4,
      fontSize: 12.5,
      color: LABEL_GREY,
      lineHeight: 1.4,
    },
    close: { position: "absolute", top: 14, right: 12, color: LABEL_GREY, "& svg": { fontSize: 20 } },
    content: { padding: "20px 24px 24px", color: c.text, fontSize: 13.5 },
    section: { "& + &": { marginTop: 22 } },
    sectionLabel: { fontSize: 11, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", color: LABEL_GREY, marginBottom: 8 },
    sectionHelp: { fontSize: 12.5, color: c.text2, marginTop: -4, marginBottom: 16 },
    box: { borderRadius: 8, padding: "14px 16px" },
    boxInfo: { background: SOFT_TEAL, border: `1px solid rgba(10,71,76,.10)` },
    boxDanger: { background: ERROR_TINT, border: `1px solid rgba(244,67,54,.18)` },
    boxHead: { display: "flex", gap: 10, alignItems: "flex-start" },
    boxIcon: { fontSize: 20, marginTop: 1, flexShrink: 0 },
    lines: { margin: 0, padding: 0, listStyle: "none", fontSize: 13.5, lineHeight: 1.55, "& li + li": { marginTop: 2 } },
    boxDivider: { borderTop: `1px solid rgba(10,71,76,.12)`, margin: "12px 0 0", paddingTop: 12 },
    checkRow: {
      display: "flex",
      gap: 12,
      alignItems: "flex-start",
      cursor: "pointer",
      "& input": { width: 16, height: 16, margin: "2px 0 0", accentColor: c.primary, flexShrink: 0, cursor: "pointer" },
    },
    checkRowBordered: { padding: "12px 14px", border: `1px solid ${c.line2}`, borderRadius: 8, "& + &": { marginTop: 8 } },
    checkLabel: { fontWeight: 600, fontSize: 13.5, color: c.text, lineHeight: 1.4 },
    checkSub: { fontSize: 12.5, color: c.text2, marginTop: 2, lineHeight: 1.45 },
    segs: { display: "inline-flex", border: `1px solid ${c.line}`, borderRadius: 8, padding: 3, gap: 3, background: "#fff" },
    seg: {
      border: 0,
      background: "none",
      font: "inherit",
      fontSize: 13,
      padding: "6px 16px",
      borderRadius: 6,
      color: c.text2,
      cursor: "pointer",
      "&:hover": { color: c.text },
      "&:focus-visible": { outline: `2px solid ${c.primary}`, outlineOffset: 1 },
      "&:disabled": { cursor: "default", opacity: 0.6 },
    },
    segOn: { background: c.primary, color: "#fff", fontWeight: 500, "&:hover": { color: "#fff" } },
    cards: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, "@media (max-width: 520px)": { gridTemplateColumns: "1fr" } },
    card: {
      textAlign: "left",
      font: "inherit",
      background: "#fff",
      border: `1px solid ${c.line}`,
      borderRadius: 8,
      padding: "12px 14px",
      cursor: "pointer",
      display: "flex",
      gap: 10,
      alignItems: "flex-start",
      "&:hover": { borderColor: c.text2 },
      "&:focus-visible": { outline: `2px solid ${c.primary}`, outlineOffset: 1 },
      "&:disabled": { cursor: "default", opacity: 0.6 },
    },
    cardOn: { borderColor: c.primary, boxShadow: `inset 0 0 0 1px ${c.primary}`, background: SOFT_TEAL, "&:hover": { borderColor: c.primary } },
    radio: { width: 16, height: 16, borderRadius: "50%", border: `1px solid ${c.line}`, background: "#fff", flexShrink: 0, marginTop: 2, boxSizing: "border-box" },
    radioOn: { border: `5px solid ${c.primary}` },
    cardTitle: { fontWeight: 600, fontSize: 13.5, color: c.text },
    cardSub: { fontSize: 12.5, color: c.text2, marginTop: 2, lineHeight: 1.4 },
    field: {
      width: "100%",
      background: "transparent",
      padding: 0,
      borderRadius: 0,
      "& + &": { marginTop: 14 },
      "&& .MuiOutlinedInput-root": { background: "#fff", borderRadius: 8, fontSize: 13.5, color: c.text },
      "&& .MuiOutlinedInput-notchedOutline": { border: `1px solid ${c.line}`, borderRadius: 8 },
      "&& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: c.text2 },
      "&& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { border: `2px solid ${c.primary}` },
      "&& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: c.error },
      "&& .MuiOutlinedInput-multiline": { padding: "4px 0" },
      "&& .MuiOutlinedInput-input": { padding: "10px 12px", lineHeight: 1.5 },
      "&& .MuiInputLabel-outlined": { fontSize: 13.5, color: c.text2, transform: "translate(12px, 12px) scale(1)" },
      "&& .MuiInputLabel-outlined.MuiInputLabel-shrink": { transform: "translate(14px, -6px) scale(0.75)" },
      "&& .MuiInputLabel-outlined.Mui-focused": { color: c.primary },
      "&& .MuiInputLabel-outlined.Mui-error": { color: c.error },
      "&& .MuiFormHelperText-root": { margin: "4px 2px 0", fontSize: 11.5, display: "flex", justifyContent: "space-between", gap: 12 },
      "&& select": { height: "auto", padding: "10px 32px 10px 12px", lineHeight: 1.5 },
      "&& select:focus": { background: "transparent" },
    },
    counter: { marginLeft: "auto", color: LABEL_GREY, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
    alert: {
      margin: "0 24px 12px",
      padding: "10px 12px",
      borderRadius: 8,
      background: ERROR_TINT,
      border: `1px solid rgba(244,67,54,.25)`,
      color: theme.palette.error.dark,
      fontSize: 13,
      display: "flex",
      gap: 8,
      alignItems: "flex-start",
      "& svg": { fontSize: 18, marginTop: 1 },
    },
    actions: { padding: "14px 24px", borderTop: `1px solid ${c.line2}`, gap: 8, margin: 0 },
    hint: { marginRight: "auto", fontSize: 12, color: LABEL_GREY },
    cancel: { fontSize: 13.5, color: c.text2, padding: "8px 14px", borderRadius: 8, "&:hover": { background: "rgba(0,0,0,.04)" } },
    confirm: {
      fontSize: 13.5,
      fontWeight: 600,
      padding: "8px 18px",
      borderRadius: 8,
      color: "#fff",
      background: c.primary,
      boxShadow: "none",
      "&:hover": { background: theme.palette.primary.dark, boxShadow: "none" },
      "&.Mui-disabled": { color: "#fff", background: "rgba(10,71,76,.45)" },
    },
    confirmDanger: {
      background: c.error,
      "&:hover": { background: theme.palette.error.dark },
      "&.Mui-disabled": { color: "#fff", background: "rgba(244,67,54,.45)" },
    },
    plain: { fontSize: 13.5, lineHeight: 1.55, color: c.text },
  };
});

export const useDialogStyles = useStyles;

let seq = 0;
const useId = (prefix) => {
  const ref = useRef(null);
  if (!ref.current) {
    seq += 1;
    ref.current = `${prefix}-${seq}`;
  }
  return ref.current;
};

// The popup frame. size: sm (forms) | xs (compact confirms). Escape / backdrop /
// close icon cancel unless busy; the first editable field gets focus.
export function ReviewDialog({
  title,
  subtitle,
  size = "sm",
  busy,
  error,
  confirmLabel,
  danger,
  confirmDisabled,
  disabledHint = "Fill the required fields",
  onConfirm,
  onCancel,
  children,
}) {
  const s = useStyles();
  const titleId = useId("sr-dialog-title");
  const contentRef = useRef(null);
  const cancel = () => {
    if (!busy) onCancel();
  };
  const focusFirst = () => {
    const root = contentRef.current;
    if (!root) return;
    // First editable field; else the selected option card.
    const el =
      root.querySelector('textarea:not([aria-hidden="true"]):not([readonly]), input[type="text"], select') ||
      root.querySelector('[role="radio"][aria-checked="true"]');
    if (el) el.focus();
  };
  return (
    <Dialog
      open
      onClose={cancel}
      disableEscapeKeyDown={!!busy}
      maxWidth={size}
      fullWidth
      scroll="paper"
      aria-labelledby={titleId}
      classes={{ paper: s.paper }}
      TransitionProps={{ onEntered: focusFirst }}
    >
      <DialogTitle disableTypography className={s.title}>
        <h2 id={titleId} className={s.titleText} style={{ margin: 0 }}>
          {title}
        </h2>
        {subtitle ? <div className={s.subtitle}>{subtitle}</div> : null}
        <IconButton aria-label="Close" className={s.close} size="small" onClick={cancel} disabled={busy}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent className={s.content} ref={contentRef}>
        {children}
      </DialogContent>
      {error ? (
        <div className={s.alert} role="alert">
          <ReportProblemOutlined />
          <span>{error}</span>
        </div>
      ) : null}
      <DialogActions className={s.actions}>
        {confirmDisabled && !busy && disabledHint ? <span className={s.hint}>{disabledHint}</span> : null}
        <Button className={s.cancel} disabled={busy} onClick={cancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          className={`${s.confirm} ${danger ? s.confirmDanger : ""}`}
          disabled={busy || confirmDisabled}
          onClick={onConfirm}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function DialogSection({ label, help, children }) {
  const s = useStyles();
  return (
    <section className={s.section}>
      {label ? <div className={s.sectionLabel}>{label}</div> : null}
      {help ? <div className={s.sectionHelp}>{help}</div> : null}
      {children}
    </section>
  );
}

// Tinted summary box: tone info (teal) | danger (red), with an icon and short lines.
export function ImpactBox({ tone = "info", icon, lines, children }) {
  const s = useStyles();
  const Icon = icon || (tone === "danger" ? ReportProblemOutlined : InfoOutlined);
  const color = tone === "danger" ? "#d32f2f" : "#0a474c";
  return (
    <div className={`${s.box} ${tone === "danger" ? s.boxDanger : s.boxInfo}`}>
      <div className={s.boxHead}>
        <Icon className={s.boxIcon} style={{ color }} />
        <ul className={s.lines}>
          {(lines || []).filter(Boolean).map((l, i) => (
            <li key={i}>{l}</li>
          ))}
        </ul>
      </div>
      {children ? <div className={s.boxDivider}>{children}</div> : null}
    </div>
  );
}

// Checkbox row: bold label + secondary line. bordered: its own outlined row.
export function CheckRow({ checked, onChange, label, secondary, disabled, bordered, help, term }) {
  const s = useStyles();
  const id = useId("sr-check");
  const row = (
    <label className={`${s.checkRow} ${bordered ? s.checkRowBordered : ""}`} htmlFor={id}>
      <input
        id={id}
        type="checkbox"
        checked={!!checked}
        disabled={disabled}
        aria-describedby={secondary ? `${id}-sub` : undefined}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <div className={s.checkLabel}>{label}</div>
        {secondary ? (
          <div id={`${id}-sub`} className={s.checkSub}>
            {secondary}
          </div>
        ) : null}
      </span>
    </label>
  );
  return help ? (
    <HelpTip help={help} term={term}>
      {row}
    </HelpTip>
  ) : (
    row
  );
}

// Two or more pill buttons acting as one radio group.
export function SegmentedToggle({ options, value, onChange, disabled, ariaLabel }) {
  const s = useStyles();
  return (
    <div className={s.segs} role="radiogroup" aria-label={ariaLabel}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={disabled}
            className={`${s.seg} ${on ? s.segOn : ""}`}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// Selectable cards (radio group): title + one-line description.
export function RadioCards({ options, value, onChange, disabled, ariaLabel }) {
  const s = useStyles();
  return (
    <div className={s.cards} role="radiogroup" aria-label={ariaLabel}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={o.label}
            disabled={disabled}
            className={`${s.card} ${on ? s.cardOn : ""}`}
            onClick={() => onChange(o.value)}
          >
            <span className={`${s.radio} ${on ? s.radioOn : ""}`} />
            <span>
              <div className={s.cardTitle}>{o.label}</div>
              {o.description ? <div className={s.cardSub}>{o.description}</div> : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// Outlined text field (label inside). min: required minimum (error only after
// touch); max: counter "48 / 600"; multiline unless single.
export function Field({ label, value, onChange, min, max, placeholder, disabled, single, required }) {
  const s = useStyles();
  const id = useId("sr-field");
  const [touched, setTouched] = useState(false);
  const len = (value || "").trim().length;
  const short = min && len < min;
  const showError = touched && short;
  return (
    <TextField
      id={id}
      className={s.field}
      variant="outlined"
      fullWidth
      label={label}
      value={value || ""}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      multiline={!single}
      minRows={single ? undefined : 2}
      maxRows={single ? undefined : 5}
      error={!!showError}
      onBlur={() => setTouched(true)}
      onChange={(e) => onChange(e.target.value)}
      InputLabelProps={placeholder ? { shrink: true } : undefined}
      inputProps={{ maxLength: max }}
      helperText={
        showError || (max && !single) ? (
          <>
            <span>{showError ? `At least ${min} characters` : ""}</span>
            {max && !single ? (
              <span className={s.counter}>
                {(value || "").length} / {max}
              </span>
            ) : null}
          </>
        ) : undefined
      }
    />
  );
}

// Outlined native select with a label.
export function SelectField({ label, value, onChange, options, placeholder, disabled }) {
  const s = useStyles();
  const id = useId("sr-select");
  return (
    <TextField
      id={id}
      className={s.field}
      variant="outlined"
      fullWidth
      select
      label={label}
      value={value || ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      SelectProps={{ native: true }}
      InputLabelProps={{ shrink: true }}
    >
      {placeholder != null ? (
        <option value="" disabled={placeholder.disabled}>
          {placeholder.label}
        </option>
      ) : null}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </TextField>
  );
}

// "About this business": the two business-context fields.
export function BusinessContextSection({ value, onChange, disabled }) {
  return (
    <DialogSection label="About this business" help="Saved for this owner (all projects) and used in future screening so similar templates aren't flagged as unsure.">
      <Field
        label="What does this business do?"
        value={value?.about}
        onChange={(v) => onChange({ ...value, about: v })}
        min={CONTEXT_MIN}
        max={CONTEXT_MAX}
        disabled={disabled}
        required
      />
      <Field
        label="What does it use WhatsApp for? (use cases)"
        value={value?.useCases}
        onChange={(v) => onChange({ ...value, useCases: v })}
        min={CONTEXT_MIN}
        max={CONTEXT_MAX}
        disabled={disabled}
        required
      />
    </DialogSection>
  );
}

// Plain explanation paragraph for compact confirms.
export function DialogText({ children }) {
  const s = useStyles();
  return <div className={s.plain}>{children}</div>;
}

// Harm category for Suspend / Reject / Delete; empty keeps the model's own label.
export function CategoryField({ value, onChange, modelLabel, disabled }) {
  const asFlagged = !HARM_OPTIONS.some((o) => o.value === modelLabel);
  return (
    <SelectField
      label="Category"
      value={value}
      onChange={onChange}
      disabled={disabled}
      options={HARM_OPTIONS}
      placeholder={
        asFlagged ? { label: modelLabel ? `As flagged (${humanizeLabel(modelLabel).toLowerCase()})` : "As flagged" } : null
      }
    />
  );
}

// "Transactional" / "Promotion" (the legit label sent with Dismiss / Mark OK).
export const LEGIT_TOGGLE = LEGIT_OPTIONS.map((o) => ({ value: o.value, label: cap(o.word) }));
